import { Spacer, Text } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import {
  Button,
  Checkbox,
  Link,
  RadioButton,
  Spinner,
  TextInput,
} from "north.js";
import "north.js/dist/style.css";
import React, { useState, useEffect } from "react";
import { Prompt } from "react-router-dom";
import styled from "styled-components";
import "twin.macro";

import {
  FormFieldLabelRow,
  OptionalLabel,
  PageWrapper,
} from "src/shared/components";
import { successToast, errorToast } from "src/shared/components/Toast";
import { useHackerContext } from "src/shared/contexts";
import { Field, PageStage } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useDeviceSize, useIsMounted } from "src/shared/hooks";
import { Nullable } from "src/shared/utils/typescript";
import { ArrowRight } from "src/static/icons";

import RSVPSidebar from "../rsvp-sidebar";

import {
  ACCEPTED_FIELDS,
  TECH_TALK_OPTIONS,
  WTW_EVENT_INTEREST_OPTIONS,
  WTW_FAMILIARITY_OPTIONS,
} from "./constants";
import DescriptionText from "./DescriptionText";

const WTW_SITE_URL = "https://waterlootechweek.ca";
const OTHER_OPTION_PREFIX = "Other: ";
const OTHER_OPTION_LABEL = "Other (please specify)";
const MAX_TECH_TALKS = 3;
const MAX_WTW_EVENT_INTEREST = 3;

const shuffle = <T,>(items: T[]): T[] => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const EventPage: React.FC = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const isMounted = useIsMounted();
  const {
    updateResponses,
    isLoading: isHackerLoading,
    navigateNext,
    navigateBack,
    hasUnsavedAutosave,
  } = useHackerContext();

  const { responsesState, setResponsesState, isValid } = useHackerState(
    ACCEPTED_FIELDS,
    {}
  );

  const [isBlocking, setIsBlocking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shuffledTechTalks] = useState(() => shuffle(TECH_TALK_OPTIONS));

  const isLoading = isHackerLoading;

  const wtwEventInterest = responsesState[Field.WTW_EVENT_INTEREST] ?? [];

  const containsOther = (arr: Nullable<string[]>) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) => item.startsWith(OTHER_OPTION_PREFIX));
  };

  const getOtherVal = (arr: Nullable<string[]>) => {
    if (!arr || arr.length === 0) return "";
    const other = arr.find((item) => item.startsWith(OTHER_OPTION_PREFIX));
    return other ? other.substring(OTHER_OPTION_PREFIX.length) : "";
  };

  const getNonOtherValues = (arr: string[]) =>
    arr.filter((item) => !item.startsWith(OTHER_OPTION_PREFIX));

  useEffect(() => {
    if (isValid && isMounted) {
      navigateNext();
    }
    if (responsesState[Field.BADGE_RSVP_STAGE] !== PageStage.COMPLETED) {
      navigateBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  const onSave = async () => {
    if (isValid) {
      try {
        const { errors } = await updateResponses({
          ...responsesState,
          [Field.EVENT_RSVP_STAGE]: PageStage.COMPLETED,
        });

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        successToast("Answers successfully saved!");
        setSubmitting(true);
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast(e as string);
        }
      }
    }
  };

  return (
    <PageWrapper>
      <Prompt
        when={isBlocking && hasUnsavedAutosave && !submitting}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      {isTabletOrSmaller && <RSVPSidebar currentRoute="Event Information" />}
      <Heading1Text>Event Information</Heading1Text>
      <Text mods="grey">
        All questions are required unless stated otherwise.
      </Text>

      <Heading3Wrapper>
        <Text mods="h3 heading">Waterloo Tech Week</Text>
      </Heading3Wrapper>
      <Text>
        <LinkStyled
          href={WTW_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <b>Waterloo Tech Week</b>
        </LinkStyled>{" "}
        is a week-long celebration of technology, startups, and innovation
        taking place <b>September 12-17, 2026</b>, leading into{" "}
        <b>Hack the North</b>. Meet founders, discover startups, build your
        network, and attend talks, workshops, demos, socials, and more
        throughout the week. Join us at the{" "}
        <b>Waterloo Tech Week Kickoff on September 11</b> to celebrate the start
        of the week!
      </Text>
      <Spacer height={24} />
      <FormFieldLabelRow>
        <Text mods="bold textBody">
          Before today, what was your familiarity with Waterloo Tech Week?
        </Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <Spacer height={12} />
      {WTW_FAMILIARITY_OPTIONS.map((option) => (
        <div key={option}>
          <RadioButton
            id={option}
            name="wtwFamiliarity"
            label={option}
            checked={responsesState[Field.WTW_FAMILIARITY] === option}
            onChange={() => {
              setIsBlocking(true);
              setResponsesState(Field.WTW_FAMILIARITY, option);
            }}
          />
          <Spacer height={12} />
        </div>
      ))}

      <Spacer height={24} />
      <FormFieldLabelRow>
        <Text mods="bold textBody">
          What types of Waterloo Tech Week events would you be most interested
          in attending? (Select up to 3)
        </Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <Spacer height={12} />
      {WTW_EVENT_INTEREST_OPTIONS.map((option) => {
        const isOther = option === OTHER_OPTION_LABEL;
        const isChecked = isOther
          ? containsOther(wtwEventInterest)
          : wtwEventInterest.includes(option);
        const isDisabled =
          wtwEventInterest.length === MAX_WTW_EVENT_INTEREST && !isChecked;

        return (
          <div key={option}>
            <Spacer height={8} />
            <Checkbox
              label={option}
              checked={isChecked}
              disabled={isDisabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setIsBlocking(true);
                if (isOther) {
                  if (e.currentTarget.checked) {
                    setResponsesState(Field.WTW_EVENT_INTEREST, [
                      ...getNonOtherValues(wtwEventInterest),
                      OTHER_OPTION_PREFIX,
                    ]);
                  } else {
                    setResponsesState(
                      Field.WTW_EVENT_INTEREST,
                      getNonOtherValues(wtwEventInterest)
                    );
                  }
                } else if (e.currentTarget.checked) {
                  setResponsesState(Field.WTW_EVENT_INTEREST, [
                    ...wtwEventInterest,
                    option,
                  ]);
                } else {
                  setResponsesState(
                    Field.WTW_EVENT_INTEREST,
                    wtwEventInterest.filter((item) => item !== option)
                  );
                }
              }}
            />
          </div>
        );
      })}
      {containsOther(wtwEventInterest) && (
        <OtherTextInputWrapper>
          <TextInput
            size="md"
            placeholder="Please specify"
            value={getOtherVal(wtwEventInterest)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIsBlocking(true);
              setResponsesState(Field.WTW_EVENT_INTEREST, [
                ...getNonOtherValues(wtwEventInterest),
                OTHER_OPTION_PREFIX + e.target.value,
              ]);
            }}
          />
        </OtherTextInputWrapper>
      )}

      <Heading3Wrapper>
        <Text mods="h3 heading">Tech Talks</Text>
      </Heading3Wrapper>
      <FormFieldLabelRow>
        <Text mods="bold textBody">
          Select up to 3 of the following Tech Talks you are interested in.
        </Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <Spacer height={12} />
      {shuffledTechTalks.map(({ label, description }) => (
        <div key={label}>
          <Spacer height={8} />
          <Checkbox
            label={label}
            checked={(responsesState[Field.TECH_TALKS] ?? []).includes(label)}
            disabled={
              (responsesState[Field.TECH_TALKS] ?? []).length ===
                MAX_TECH_TALKS &&
              !(responsesState[Field.TECH_TALKS] ?? []).includes(label)
            }
            onChange={(e: any) => {
              setIsBlocking(true);
              if (e.currentTarget.checked) {
                setResponsesState(Field.TECH_TALKS, [
                  ...(responsesState[Field.TECH_TALKS] ?? []),
                  label,
                ]);
              } else {
                setResponsesState(
                  Field.TECH_TALKS,
                  (responsesState[Field.TECH_TALKS] ?? []).filter(function (
                    item
                  ) {
                    return item !== label;
                  })
                );
              }
            }}
          />
          <DescriptionText text={description} />
        </div>
      ))}
      <Spacer height={100} />
      <FlexRow>
        <Button color="primary" disabled={isLoading} onClick={onSave} size="md">
          {isLoading ? (
            <Spinner />
          ) : (
            <FlexRow>
              <p>
                {responsesState[Field.EVENT_RSVP_STAGE] === PageStage.COMPLETED
                  ? "Save"
                  : "Next"}
              </p>
              <Spacer width="8px" />
              <ArrowRight width={16} />
            </FlexRow>
          )}
        </Button>
      </FlexRow>
      <Spacer height={24} />
    </PageWrapper>
  );
};

const Heading1Text = styled.h1`
  margin-top: 0;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-weight: 900;
  font-size: 28px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 24px;
  `}
  ${({ theme }) => theme.mediaQueries.mobile`
    font-size: 20px;
  `}
`;

const Heading3Wrapper = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: 42px;
  margin-bottom: 24px;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

const LinkStyled = styled(Link)`
  font-weight: 600;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary} !important;
`;

const OtherTextInputWrapper = styled.div`
  margin-top: 8px;
  margin-left: 24px;
`;

export default EventPage;
